import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardBody } from 'reactstrap';

import AppSettingsService from 'services/appSettings';
import CancelModal from 'components/manageSessionModal/cancelModal';
import { toUkDisplayDate } from 'helpers/ukDate';

export const GP_LETTER_MODES = [
    {
        value: 'off',
        label: 'Off',
        description: 'Nothing is queued or sent. Discharge letters are sent by hand, as before.',
    },
    {
        value: 'dry-run',
        label: 'Dry run',
        description: 'Every decision is made and recorded, and nothing is sent. Letters handled in dry run are never sent later.',
    },
    {
        value: 'live',
        label: 'Live',
        description: 'Letters are sent automatically to the patient\'s GP practice.',
    },
];

const labelFor = value => (GP_LETTER_MODES.find(mode => mode.value === value) || {}).label || value;

/**
 * Off / dry run / live for the automatic discharge letter. superAdmin only - the page renders this
 * card for no one else, and the API refuses anyone else too. Takes effect immediately.
 */
export default function GpLetterAutoSendCard() {
    const [setting, setSetting] = useState(null);
    const [error, setError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [confirmLive, setConfirmLive] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await AppSettingsService.getSettings();
            setSetting(data.gpLetterAutoSendMode);
            setError('');
        } catch (e) {
            setError('Could not load this setting.');
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async (mode) => {
        setIsSaving(true);
        try {
            const { data } = await AppSettingsService.setGpLetterAutoSendMode(mode);
            setSetting(data.gpLetterAutoSendMode);
            setError('');
        } catch (e) {
            setError('Could not change the setting.');
        } finally {
            setIsSaving(false);
            setConfirmLive(false);
        }
    };

    const choose = (mode) => {
        if (!setting || mode === setting.value || isSaving) {
            return;
        }
        // Live sends patients' notes to GP practices without anyone looking first. Ask.
        if (mode === 'live') {
            setConfirmLive(true);
            return;
        }
        save(mode);
    };

    return (
        <Card className="card-auth overflow-hidden" data-testid="gp-letter-auto-send-card">
            <CardBody>
                <label className="details-caption p-2">Automatic discharge letters</label>
                <div className="p-2">
                    {error ? <div className="text-danger mb-2" role="alert">{error}</div> : null}

                    {setting ? (
                        <>
                            <div className="mh-options" role="radiogroup" aria-label="Automatic discharge letters">
                                {GP_LETTER_MODES.map(mode => (
                                    <label
                                        key={mode.value}
                                        className={`mh-option${setting.value === mode.value ? ' mh-option--selected' : ''}`}
                                    >
                                        <input
                                            type="radio"
                                            name="gpLetterAutoSendMode"
                                            checked={setting.value === mode.value}
                                            disabled={isSaving}
                                            onChange={() => choose(mode.value)}
                                        />
                                        <span>{mode.label}</span>
                                    </label>
                                ))}
                            </div>

                            <p className="text-muted font-size-12 mt-2 mb-1" data-testid="gp-letter-mode-description">
                                {(GP_LETTER_MODES.find(mode => mode.value === setting.value) || {}).description}
                            </p>

                            <p className="text-muted font-size-12 mb-1" data-testid="gp-letter-mode-source">
                                {setting.source === 'setting'
                                    ? `Set to ${labelFor(setting.value)}${setting.updatedBy ? ` by ${setting.updatedBy.name}` : ''}`
                                        + `${setting.updatedAt ? ` on ${toUkDisplayDate(setting.updatedAt)}` : ''}.`
                                    : `Not set here yet - using the configured default (${labelFor(setting.configValue)}).`}
                            </p>

                            {setting.testInbox ? (
                                <p className="font-size-12 mb-0" data-testid="gp-letter-test-inbox">
                                    Test environment: every GP letter goes only to <strong>{setting.testInbox}</strong>,
                                    never to a real practice.
                                </p>
                            ) : null}
                        </>
                    ) : (!error && <div className="text-muted">Loading…</div>)}
                </div>
            </CardBody>

            <CancelModal
                visibleModal={confirmLive}
                setVisibleModal={setConfirmLive}
                handleClose={() => save('live')}
                header="Switch automatic letters to Live?"
                text="Discharge letters will be sent to patients' GP practices automatically, with the consultation notes attached, whenever the patient consented and their surgery is recognised."
                cancelButtonTitle="Cancel"
            />
        </Card>
    );
}
